import React, { useMemo } from 'react'
import { Map } from 'immutable'
import type { Map as ImmutableMap } from 'immutable'
import compose from 'recompose/compose'
import { connect as connectRedux } from 'react-redux'
import { connect as connectStaticText } from '../../StaticText/connect'
import { H2, HEADING_TYPES } from '../../Heading/index'
import Button, { TYPES } from '../../Button/index'
import { getBoundActions } from '../../../actions/index'
import { selectMostRecentEligibleEntitlementFromUser } from '../../../services/user/selectors'
import { getFormattedLocaleDate } from '../../../services/date-time/index'
import { getPrimary } from '../../../services/languages/index'
import { URL_ACCOUNT_UPDATE_PAYMENT } from '../../../services/url/constants'
import { DTP_TEMP_ENTITLEMENT_CTA_CLICKED_GA4_EVENT } from '../../../services/event-tracking/index'

interface ModalPaymentFailedProps {
  user: ImmutableMap<string, unknown>
  staticText: ImmutableMap<string, unknown>
  dismissModal: () => void
  setEventDefault: (event: unknown) => void
}

function ModalPaymentFailed ({
  user,
  staticText,
  setEventDefault,
}: ModalPaymentFailedProps) {
  const userLanguage = getPrimary(user.getIn(['data', 'language']))

  const entitlementEndDate = useMemo(() => {
    const mostRecentEntitlement = selectMostRecentEligibleEntitlementFromUser(user)
    if (mostRecentEntitlement === undefined || !Map.isMap(mostRecentEntitlement)) {
      return ''
    }
    const entitlementEndDateString = mostRecentEntitlement.get('end')
    const entitlementEndInMilliseconds = entitlementEndDateString
      ? parseInt(entitlementEndDateString, 10) * 1000
      : 0

    return entitlementEndInMilliseconds
      ? getFormattedLocaleDate(new Date(entitlementEndInMilliseconds), userLanguage)
      : ''
  }, [user, userLanguage])

  const bodyTemplate = staticText.getIn(['data', 'body'], '')
  const body = bodyTemplate.replace(/\{ entitlementEndDate \}/g, entitlementEndDate)

  const handleUpdatePaymentMethodClick = () => {
    setEventDefault(DTP_TEMP_ENTITLEMENT_CTA_CLICKED_GA4_EVENT.mergeDeep({
      ui_element: 'Update Payment Method Button',
      ui_location: 'Payment Failed Modal',
    }))
  }

  return (
    <div className="modal-payment-failed">
      <H2 as={HEADING_TYPES.H4} className="modal-payment-failed__title">
        {staticText.getIn(['data', 'title'])}
      </H2>
      <p className="modal-payment-failed__body">{body}</p>
      <div className="modal-payment-failed__buttons">
        <Button
          className="modal-payment-failed__button modal-payment-failed__update-payment-method"
          type={TYPES.PRIMARY}
          url={URL_ACCOUNT_UPDATE_PAYMENT}
          onClick={handleUpdatePaymentMethodClick}
          scrollToTop
        >
          {staticText.getIn(['data', 'updatePaymentMethod'])}
        </Button>
      </div>
    </div>
  )
}

export default compose(
  connectStaticText({ storeKey: 'modalPaymentFailed' }),
  connectRedux(
    (state: { user: ImmutableMap<string, unknown> }) => ({
      user: state.user,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setEventDefault: actions.eventTracking.setEventDefault,
      }
    },
  ),
)(ModalPaymentFailed as React.ComponentType<unknown>) as React.ComponentType<
  Pick<ModalPaymentFailedProps, 'dismissModal'>
>
